import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminFetchInit } from '../../config/adminApi';

/**
 * Resolve visual badge for OpenSanctions sync status.
 * @param {{ status?: string, last_sync_at?: string | null, records_count?: number | null }} sync
 */
export function resolveOpenSanctionsBadge(sync) {
  const records = sync?.records_count;
  const recordsN = records == null ? null : Number(records);
  if (recordsN == null || !Number.isFinite(recordsN) || recordsN <= 0) {
    return {
      label: 'Status: Unloaded / Missing DB',
      className: 'border-amber-400/40 bg-amber-950/40 text-amber-100',
      unloaded: true,
    };
  }

  const status = String(sync?.status || '').toUpperCase();
  const lastAt = sync?.last_sync_at ? Date.parse(sync.last_sync_at) : NaN;
  const ageMs = Number.isFinite(lastAt) ? Date.now() - lastAt : null;
  const outdated = ageMs != null && ageMs > 36 * 60 * 60 * 1000;

  if (status === 'ERROR' || outdated) {
    return {
      label: outdated && status !== 'ERROR' ? 'OUTDATED' : 'ERROR',
      className: 'border-red-400/40 bg-red-950/40 text-red-200',
      unloaded: false,
    };
  }
  if (status === 'UNCHANGED') {
    return {
      label: 'UNCHANGED',
      className: 'border-amber-400/35 bg-amber-950/35 text-amber-100',
      unloaded: false,
    };
  }
  if (status === 'SUCCESS') {
    return {
      label: 'UP-TO-DATE',
      className: 'border-emerald-400/40 bg-emerald-950/40 text-emerald-200',
      unloaded: false,
    };
  }
  return {
    label: 'UNKNOWN',
    className: 'border-white/10 bg-slate-950/60 text-slate-300',
    unloaded: false,
  };
}

function formatSyncTime(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
}

/**
 * Admin Overview panel: OpenSanctions local DB sync witness + manual trigger.
 *
 * @param {{ baseUrl: string }} props
 */
export default function OpenSanctionsSyncMonitor({ baseUrl }) {
  const [sync, setSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    setError('');
    const res = await fetch(`${baseUrl}/admin/compliance-sync-status`, adminFetchInit());
    if (!res.ok) throw new Error(`compliance-sync-status HTTP ${res.status}`);
    const data = await res.json();
    setSync(data?.sync || null);
  }, [baseUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await loadStatus();
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load sync status');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadStatus]);

  const badge = useMemo(() => resolveOpenSanctionsBadge(sync || {}), [sync]);

  const triggerSync = useCallback(async () => {
    setSyncing(true);
    setError('');
    try {
      const res = await fetch(
        `${baseUrl}/admin/sync-opensanctions`,
        adminFetchInit({ method: 'POST' }),
      );
      const data = await res.json().catch(() => ({}));
      if (data?.sync) setSync(data.sync);
      else await loadStatus();
      if (!res.ok || data?.ok === false) {
        setError(data?.error || data?.sync?.last_error || `Sync failed (HTTP ${res.status})`);
      }
    } catch (err) {
      setError(err?.message || 'Sync request failed');
    } finally {
      setSyncing(false);
    }
  }, [baseUrl, loadStatus]);

  return (
    <section className="rounded-2xl border border-white/10 bg-slate-900/55 p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">
            OpenSanctions Sync Monitor
          </h2>
          <p className="mt-1 text-[11px] text-slate-500">
            Local AML database (`targets.simple.csv`) · Beat schedule 03:00 UTC daily
          </p>
        </div>
        <span
          className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${badge.className}`}
        >
          {loading ? '…' : badge.label}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Last sync</p>
          <p className="mt-1 text-sm font-semibold text-slate-800">
            {loading ? '—' : formatSyncTime(sync?.last_sync_at)}
          </p>
        </div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">File size</p>
          <p className="mt-1 text-sm font-semibold text-slate-800">
            {loading ? '—' : `${Number(sync?.file_size_mb ?? 0).toFixed(1)} MB`}
          </p>
        </div>
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Active sanctions records
          </p>
          <p className="mt-1 text-sm font-semibold tabular-nums text-slate-800">
            {loading ? '—' : Number(sync?.records_count ?? 0).toLocaleString()}
          </p>
        </div>
      </div>

      {sync?.message ? (
        <p className="mt-3 text-[11px] text-slate-400">
          Status detail: <span className="font-semibold">{sync.message}</span>
          {sync.last_error ? (
            <span className="text-red-600"> · {sync.last_error}</span>
          ) : null}
        </p>
      ) : null}

      {error ? (
        <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-semibold text-red-700">
          {error}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={triggerSync}
          disabled={syncing || loading}
          className="inline-flex items-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-950/40 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-indigo-800 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {syncing ? (
            <>
              <span
                className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-indigo-300 border-t-indigo-700"
                aria-hidden
              />
              Trigger Sync Now
            </>
          ) : (
            'Trigger Sync Now'
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            setLoading(true);
            loadStatus()
              .catch((err) => setError(err?.message || 'Refresh failed'))
              .finally(() => setLoading(false));
          }}
          disabled={syncing}
          className="rounded-lg border border-white/10 bg-slate-900/55 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-400 hover:bg-slate-50 disabled:opacity-60"
        >
          Refresh
        </button>
        {syncing ? (
          <p className="text-[11px] font-semibold text-indigo-800" role="status">
            Syncing sanctions in background...
          </p>
        ) : null}
      </div>
    </section>
  );
}
